import { action, computed, makeObservable, observable } from 'mobx';
import { BookmarkBrowserState } from '../../components/bookmarks/bookmarkBrowserState';
import { Nullable } from '../../core';
import { BookmarkList } from '../../entities';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { Error, loadError, pageError } from '../../core/error';
import { isAgencyAccessRefused } from '../../api/apiErrorUtils';

export type BookmarksPageMode =
  'bookmarkListCatalog' |
  'bookmarkCatalog';

export class BookmarksPageState
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'setError' | 'setLoaded'>(this, {
      isLoading: observable,
      error: observable,
      mode: observable,
      targetId: observable,
      pageSize: observable,
      bookmarkList: computed,
      catalogBookmarkLists: computed,
      pageMounted: action,
      setPageSize: action,
      pageUnmounted: action,
      reset: action,
      setError: action,
      setLoaded: action,
    });
  }

  isLoading = false;
  error: Error | null = null;

  mode: Nullable<BookmarksPageMode> = null;
  targetId: Nullable<string> = null;
  pageSize: number = 20;
  readonly browser = new BookmarkBrowserState(this.store);

  get bookmarkList(): Nullable<BookmarkList> {
    const id = this.targetId;
    if (!id || !this.store.hasBookmarkList(id))
      return null;
    return this.store.getBookmarkList(id);
  }

  get catalogBookmarkLists(): BookmarkList[] {
    return this.store.bookmarkLists
      .sort((a, b) => b.createdAtTimestamp - a.createdAtTimestamp);
  }

  async pageMounted(mode: BookmarksPageMode, targetId?: string) {

    this.reset();
    this.isLoading = true;

    this.mode = mode;
    this.targetId = targetId || null;

    switch (mode) {
      case 'bookmarkListCatalog': {
        const [, err1] = await this.store.apiFetchBookmarkLists();
        if (err1)
          return this.setFetchError(err1);

        const bookmarkLists = this.store.bookmarkLists;
        if (bookmarkLists.length === 1) {
          const [, err2] = await this.store.apiFetchBookmarks({ listId: bookmarkLists[0].id });
          if (err2)
            return this.setFetchError(err2);
        }
      } break;

      case 'bookmarkCatalog': {
        if (!targetId)
          return this.setError();

        const [, err1] = await this.store.apiFetchBookmarkList(targetId);
        const [, err2] = await this.store.apiFetchBookmarks({ listId: targetId });

        const err = err1 ?? err2;
        if (err)
          return this.setFetchError(err);
        if (!this.bookmarkList)
          return this.setError();

      } break;
    }

    this.setLoaded();
  }

  async setPageSize(pageSize: number) {
    if (this.pageSize !== pageSize) {
      this.pageSize = pageSize;
      switch (this.mode) {
        case 'bookmarkListCatalog': {
          const bookmarkLists = this.store.bookmarkLists;
          if (bookmarkLists.length === 1) {
            const [, err2] = await this.store.apiFetchBookmarks({
              listId: bookmarkLists[0].id,
              first: pageSize
            });
            if (err2)
              return this.setFetchError(err2);
          }
        } break;
        case 'bookmarkCatalog': {
          if (!this.targetId)
            return this.setError();

          const [, err1] = await this.store.apiFetchBookmarkList(this.targetId);
          const [, err2] = await this.store.apiFetchBookmarks({
            listId: this.targetId,
            first: pageSize
          });

          const err = err1 ?? err2;
          if (err)
            return this.setFetchError(err);
          if (!this.bookmarkList)
            return this.setError();

        } break;
      }
    }
  }

  pageUnmounted() {
    this.reset();
  }


  // #region State helpers
  reset() {
    this.mode = null;
    this.targetId = null;
    this.isLoading = false;
    this.error = null;
  }

  private setError(error?: Error) {
    if (!error)
      error = pageError();

    this.isLoading = false;
    this.error = error;
  }

  /** A refused list keeps the permissions message; anything else is a failed load the user can retry. */
  private setFetchError(err: Error) {
    return this.setError(isAgencyAccessRefused(err) ? undefined : loadError('your bookmarks'));
  }

  private setLoaded() {
    this.isLoading = false;
    this.error = null;
  }
  // #endregion
}